import React,  { useRef } from "react";
import headerImage from "../../assets/header4.jpg";
import { CopyBlock, dracula } from "react-code-blocks";
import w10_1 from "../../assets/w10_1.jpg";
import w10_2 from "../../assets/w10_2.jpg";
import output from "../../assets/output.mp4";
import w10_3 from "../../assets/w10_3.jpg";


const Assignment11 = () => {
  const i2c = useRef(null);
  const display = useRef(null);
  const motordriver = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };


    
  return (

    <div className="container mx-auto my-8 px-5 max-w-3xl bg-gray-100 p-8 rounded-lg shadow-md">
      {/* Header */}
      <div className="relative mb-8">
        <img
          src={headerImage}
          alt="Header"
          className="w-full h-64 object-cover rounded-lg shadow-md  object-cover filter grayscale brightness-75"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 rounded-b-lg">
          <h1 className="text-4xl font-bold text-bg-secondary"  style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 1)' }}>10. Week - Output Devices </h1>
          <p className="text-gray-300 mt-2">Published on: <span className="font-semibold">March 26, 2025</span></p>
          <div className="mt-4 flex space-x-4">
         {/* Navigation Links */}
      <div className="mt-4 flex space-x-4">
        <button onClick={() => scrollToSection(i2c)} className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary">
         I²C
        </button>
        <button onClick={() => scrollToSection(display)} className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary">
          I²C Display
        </button>
        <button onClick={() => scrollToSection(motordriver)} className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary">
          Motor Driver
        </button>

 
        </div>
        </div>
        </div>
        </div>
        <p className="text-justify text-gray-700 mb-6">
  In this week’s assignment, the focus was on <strong>output devices</strong> — components that allow a microcontroller to communicate with the outside world by producing a visual, audible, or mechanical response. This can include anything from LEDs and motors to speakers and displays.
</p>
<div className="text-gray-700 space-y-4 mt-6">

<p className="text-justify text-gray-700 mb-6">
  The goal was to connect and control at least one output device using a board we previously designed or modified. In my case, I continued working with my custom ESP32S3 Plus-based development board and chose to explore an <strong>I2C LCD display</strong> as an output device. 
</p>
<p className="text-justify text-gray-700 mb-4">
        As always you will find our group assignment on our
        <a 
          href="https://fabacademy.org/2025/labs/bottrop/Assignments/week10/" 
          className="text-blue-500 underline ml-1" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          group documentation page 
        </a>. This week, we measured the power consumption of an LED strip depending on the color output.
      </p>
      <p className="text-justify text-gray-700 mb-4">
        I used my development board I produced for  
        <a 
          href="https://fabacademy.org/2025/labs/bottrop/students/annasophia-brokuf/assignments/10" 
          className="text-blue-500 underline ml-1" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          input week
        </a>.
      </p>

 {/* i²C Section*/}
<div ref={i2c}></div>
{/* i²C Section */}
<h2 className="text-2xl font-bold text-left mb-2">What is I²C and how does it work?</h2>
<p className="text-justify mb-4">
  I²C (Inter-Integrated Circuit) is a super handy communication protocol that lets microcontrollers talk to sensors, displays, and other modules using just two wires: <b>SDA</b> (data) and <b>SCL</b> (clock). It’s perfect for connecting multiple devices without a mess of cables.
</p>
<ul className="list-disc pl-6 space-y-1">
  <li><p><b>How it works:</b> The master device sends a clock signal and controls the data flow, while slaves respond.</p></li>
  <li><p><b>Master/slave concept:</b> One master can manage several slaves — each waits for its turn to communicate.</p></li>
  <li><p><b>Addressing:</b> Every I²C device has a unique address so the master knows who to talk to.</p></li>
  <li><p><b>Connecting multiple I²C devices:</b> You can hook up multiple modules on the same two lines, just avoid address conflicts.</p></li>
</ul>

         

   {/* Display Section*/}
  <div ref={display}></div>
  <h2 className="text-2xl font-bold text-left mb-2">Displaying Sensor Values on the I2C LCD</h2>

  <p>
    As part of the output devices week and to prepare for my final project, I wanted to display the distance readings from two ultrasonic sensors on an I2C LCD display. 
  </p>
  <h3 className="text-xl font-semibold mt-4"></h3>

  <h3 className="text-xl font-semibold mt-4">Wiring the I2C LCD Display</h3>
  <p>
    I used a common 16x2 LCD display with an I2C interface backpack. It only requires four connections:
  </p>

  <ul className="list-disc pl-6 space-y-1">
    <li><strong>GND</strong> → GND on DevBoard</li>
    <li><strong>VCC</strong> → 5V or 3.3V on DevBoard</li>
    <li><strong>SDA</strong> → SDA pin (GPIO5)</li>
    <li><strong>SCL</strong> → SCL pin (GPIO6)</li>
  </ul>

  <h3 className="text-xl font-semibold mt-4">Initial Setup</h3>
  <CopyBlock
              text={`//Initial Setup
#include <LiquidCrystal_I2C.h>

LiquidCrystal_I2C lcd(0x27, 16, 2); 

void setup() {
  lcd.init();       // initialize the LCD
  lcd.clear();      // clear the LCD display
  lcd.backlight();  // make sure backlight is on
  lcd.setCursor(0, 0);
  lcd.print("Starting...");
  delay(1000);
}

void loop(){
}
                `}
              language="C"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
  <p>
    At first, the display remained dark but I noticed the backlight flashed briefly every second. After checking all connections, I found that the GND was not properly connected. Using a different GND pin fixed the issue.
  </p>

  <h3 className="text-xl font-semibold mt-4">Finding the I2C Address</h3>
  <p>
    Before I could communicate with the display, I used an <strong>I2C scanner sketch</strong> to find its address. It returned <code>0x27</code>, which is a common default for many I2C LCD modules.
  </p>

  <h3 className="text-xl font-semibold mt-4">Adjusting the Contrast</h3>
  <p>
    Even with correct wiring and code, the display remained dark until I manually adjusted the potentiometer on the back of the display. After tweaking the contrast, the characters became visible.
  </p>


  <h3 className="text-xl font-semibold mt-4">Display Sensor Values </h3>
  <p>
    I wanted to show the values from both sensors on the 2-line display. The first version used <code>lcd.clear()</code> in every loop and used a delay of 500ms, which caused flickering and a slowly updating display:
  </p>
            <CopyBlock
              text={
  `//First Code Snippet for displaying sensor data
  lcd.clear();
  lcd.setCursor(0, 0);
  lcd.print("L: ");
  lcd.print(distanceCmL);
  lcd.print("cm");
  
  lcd.setCursor(0, 1);
  lcd.print("R: ");
  lcd.print(distanceCmR);
  lcd.print("cm");
  
  delay(500);
                `}
              language="C"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
  
<p>I noticed that without using clear(), the digits would shift when displaying different values, which sometimes resulted in "cmm" being printed. To fix this issue, I decided to place the unit ("cm") at a fixed position on the display.</p>
  
  <p>
    To improve readability and reduce flickering, I removed <code>lcd.clear()</code> and fixed the "cm" to a constant cursor position, updating only the numeric part:
  </p>
  <CopyBlock
              text=
                {`// Improvements  
  char buffer[7]; 
  dtostrf(distanceCmL, 6, 2, buffer);
  lcd.setCursor(3, 0);
  lcd.print(buffer);
  lcd.setCursor(10, 0);
  lcd.print("cm");
  delay(200);
                  `}
              language="C"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
  <pre className="bg-gray-100 text-sm p-3 rounded overflow-x-auto">

  </pre>

<p className="text-justify text-gray-700 mb-6">
To ensure the measurement values are consistently aligned and avoid visual glitches like "cmm" due to shifting digits, I used a fixed-length <code>char</code> array as a temporary buffer. The function <code>dtostrf(distanceCmL, 6, 2, buffer)</code> converts the floating-point distance value into a right-aligned string with a total width of 6 characters and 2 decimal places, and stores it in the buffer. This prevents leftover characters when the number of digits changes.
</p>

<p className="text-justify text-gray-700 mb-6">
  The command <code>lcd.setCursor(3, 0)</code> positions the cursor at column <strong>3</strong> of row <strong>0</strong> (top row). This fixed placement ensures consistent alignment of the numeric value. Similarly, <code>lcd.setCursor(10, 0)</code> places the cursor at column <strong>10</strong> to append the unit <code>"cm"</code> after the numeric value. By splitting the output into numeric value and unit and using fixed positions, the layout remains stable even when the number of digits varies.
</p>

<img src={w10_1} alt="" className="my-4 mx-auto rounded shadow-md" />
<p>You can find my code for displaying the ultrasonic sensors in the download section.
The wiring and functionality of the ultrasonic sensors were already explained in detail in previous weeks. </p>

<img src={w10_2} alt="" className="my-4 mx-auto rounded shadow-md" />

  <h3 className="text-xl font-semibold mt-4">Final Setup</h3>
  <p>
    The final display shows the left sensor on line 1 and the right sensor on line 2. The refresh rate is almost fast enough for real-time feedback, and no more flickering or ghost characters appear.
  </p>
<video controls className="my-4 mx-auto rounded shadow-md">
  <source src={output} type="video/mp4" />
</video>

  <p>
    This was a successful first prototype for measuring and displaying the values of the ultrasonic sensors for my robotic project. 
  </p>
</div>


        {/* Motor Driver Section*/}
        <div ref={motordriver}></div>
        <h2 className="text-2xl font-bold text-left mb-2">Stepper Motor & Motor Driver</h2>
        <p className="text-justify text-gray-700 mb-6">

        After successfully implementing the display output, my next step was to start designing the motor control board. Unfortunately, the package containing my components was tampered with, and the ESP32 modules I had ordered were stolen. This unexpected issue has delayed my progress slightly, but I'm already working on getting replacements as soon as possible.
      
      </p>
      <img src={w10_3} alt="" className="my-4 mx-auto rounded shadow-md" />
      <h2 className="text-2xl font-bold text-left mb-2">Downloads</h2>

      <a
    style={{ display: "block" }}
    href="https://fabacademy.org/2025/labs/bottrop/students/annasophia-brokuf/files/Ultrasonic_Sketch_with_Display.ino"
    download
  >
    📎 Download Code
  </a>
        </div>



  );
};

export default Assignment11;
